/**
 * `remark-math`'s single-dollar inline math (`$...$`) has no way to tell currency
 * ("$10,000") apart from math ("$C_L$") on its own — worse, its tokenizer allows
 * whitespace inside the content, so `$5 and $10` gets swallowed into a single
 * (bogus) math span rather than left alone.
 *
 * This mirrors the convention GitHub/MathJax use to disambiguate the two:
 * treat `$...$` as math only if the character immediately after the opening `$`
 * and the character immediately before the closing `$` are both non-whitespace.
 * Currency almost always fails this — there's a space (or end of the amount)
 * before the *next* `$` in the sentence ("$10,000 to $20,000"), so neither `$`
 * finds a same-line partner whose boundaries qualify. Real inline math like
 * `$C_L$` or `$2\pi$` naturally satisfies it because the delimiters hug the
 * content tightly.
 *
 * Any `$` that isn't part of a qualifying pair (and isn't a `$$...$$` block math
 * delimiter, and isn't already escaped) gets escaped so remark-math leaves it as
 * plain text instead of misparsing it.
 *
 * Trade-off: a tightly-packed currency range like `$50-$75` (no space around the
 * hyphen) will still be mistaken for math, and math starting/ending flush against
 * punctuation with no space can occasionally slip through either way — this is a
 * heuristic, not a perfect parser, and matches the same limitation GitHub's own
 * convention has.
 */
const MATH_OR_DOLLAR = /(?<!\\)\$\$[\s\S]*?\$\$|(?<!\\)\$(?!\$)\S(?:[^$\r\n]*?\S)?\$(?!\$)|(?<!\\)\$/g;

export function escapeCurrencyDollars(markdown: string): string {
  return markdown.replace(MATH_OR_DOLLAR, (match) => (match === '$' ? '\\$' : match));
}
